import React from 'react';
import {DataGrid} from '@mui/x-data-grid';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { formatDateToJerusalem } from '../../../../utils/dateFormatter';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Skeleton,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import IntegrationSourceChip from 'components/IntegrationSourceChip';
import ApiSuccessChip from 'components/ApiSuccessChip';
import DuplicateDataIndicator from 'components/DuplicateDataIndicator';

export default function RowEditControlGrid({
  dataToShow,
  setPageSize,
  pageSize,
  setPageNumber,
  pageNumber,
  rowCount,
  onEditCommit,
  setPatchEdited,
  gettingData,
  handleOnSelect,
  userRole,
  formatDate: externalFormatDate,
  selected,
  onOpenLeadHistory,
}) {
  const [editRowsModel, setEditRowsModel] = React.useState({});

  // State for edit modal
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [editFormData, setEditFormData] = React.useState({});

  // State for other data modal
  const [otherDataModalOpen, setOtherDataModalOpen] = React.useState(false);
  const [otherDataContent, setOtherDataContent] = React.useState(null);

  // Handler for showing other data modal
  const handleShowOtherData = (otherData) => {
    setOtherDataContent(otherData);
    setOtherDataModalOpen(true);
  };

  // Handler for closing other data modal
  const handleCloseOtherDataModal = () => {
    setOtherDataModalOpen(false);
    setOtherDataContent(null);
  };

  // Skeleton loading component
  const SkeletonDataGrid = () => (
    <Box sx={{width: '100%'}}>
      {/* Toolbar skeleton */}
      <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Box display='flex' gap={1}>
            <Skeleton variant='rectangular' width={120} height={36} />
            <Skeleton variant='rectangular' width={100} height={36} />
            <Skeleton variant='rectangular' width={100} height={36} />
            <Skeleton variant='rectangular' width={100} height={36} />
          </Box>
          <Box display='flex' gap={1}>
            <Skeleton variant='circular' width={32} height={32} />
            <Skeleton variant='circular' width={32} height={32} />
            <Skeleton variant='circular' width={32} height={32} />
            <Skeleton variant='circular' width={32} height={32} />
          </Box>
        </Box>
      </Box>

      {/* Table skeleton */}
      <Box sx={{p: 2}}>
        {/* Header row */}
        <Box display='flex' gap={1} mb={1}>
          <Skeleton variant='rectangular' width={40} height={24} />
          <Skeleton variant='text' width={120} height={24} />
          <Skeleton variant='text' width={150} height={24} />
          <Skeleton variant='text' width={100} height={24} />
          <Skeleton variant='text' width={80} height={24} />
          <Skeleton variant='text' width={100} height={24} />
          <Skeleton variant='text' width={80} height={24} />
          <Skeleton variant='text' width={100} height={24} />
          <Skeleton variant='text' width={80} height={24} />
          <Skeleton variant='text' width={60} height={24} />
        </Box>

        {/* Data rows */}
        {Array.from({length: 8}).map((_, index) => (
          <Box key={index} display='flex' gap={1} mb={1} alignItems='center'>
            <Skeleton variant='rectangular' width={40} height={24} />
            <Skeleton variant='text' width={120} height={24} />
            <Skeleton variant='text' width={150} height={24} />
            <Skeleton variant='text' width={100} height={24} />
            <Skeleton variant='text' width={80} height={24} />
            <Skeleton variant='text' width={100} height={24} />
            <Skeleton variant='text' width={80} height={24} />
            <Skeleton variant='text' width={100} height={24} />
            <Skeleton variant='text' width={80} height={24} />
            <Box display='flex' gap={0.5}>
              <Skeleton variant='circular' width={24} height={24} />
              <Skeleton variant='circular' width={24} height={24} />
              <Skeleton variant='circular' width={24} height={24} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  // Handle double-click to open edit modal
  const handleRowDoubleClick = (params) => {
    const rowData = params.row;
    setEditingRow(rowData);
    setEditFormData({...rowData});
    setEditModalOpen(true);
  };

  // Handle edit form data changes
  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle save edit
  const handleSaveEdit = () => {
    if (onEditCommit && editingRow) {
      // Create the patchEdited object structure expected by onEditCommit
      const updatedPatchEdited = {
        [editingRow.id]: {},
      };

      // Convert editFormData to the expected format
      const excludedFields = [
        'id',
        'created_at',
        'updated_at',
        'Rowkey',
        'sent_at',
        'customer_name',
        'customer_id',
        'customerId',
        'user_name',
        'userName',
        'custmoer_name',
        'custmoerName',
        'custmoer_id',
        'custmoerId',
        'Custmoer_Name',
        'CUSTMOER_NAME',
        'CustmoerName',
        'domain_name',
        'domain_id',
        'domin_id',
        'domainId',
        'dominId',
        'integration_source',
        'formId',
      ];

      Object.keys(editFormData).forEach((key) => {
        // Exclude if in list or contains 'customer' or 'user' in the key name
        if (
          !excludedFields.includes(key) &&
          !key.toLowerCase().includes('customer') &&
          !key.toLowerCase().includes('user')
        ) {
          let value = editFormData[key];

          // Try to parse other_data as JSON if it looks like JSON
          if (key === 'other_data' && value) {
            try {
              const trimmedValue = value.trim();
              if (
                trimmedValue.startsWith('{') ||
                trimmedValue.startsWith('[')
              ) {
                value = JSON.parse(trimmedValue);
              }
            } catch (e) {
              // Keep as string if JSON parse fails
              console.log('other_data is not valid JSON, keeping as string');
            }
          }

          updatedPatchEdited[editingRow.id][key] = {value: value};
        }
      });

      // Update the patchEdited state first
      setPatchEdited((prev) => ({
        ...prev,
        ...updatedPatchEdited,
      }));

      // Call onEditCommit with the updated data directly
      onEditCommit(editingRow.id, updatedPatchEdited);
    }
    setEditModalOpen(false);
    setEditingRow(null);
    setEditFormData({});
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditModalOpen(false);
    setEditingRow(null);
    setEditFormData({});
  };

  // Date formatting function
  const formatDate = (date) => {
    return formatDateToJerusalem(date);
  };
  // Helper function to get column width
  function getColumnWidth(key) {
    const widthMap = {
      id: 80,
      fullName: 200,
      email: 200,
      phone: 150,
      data_source: 150,
      integration_source: 180,
      api_success: 120,
      age: 80,
      country: 120,
      marks: 100,
      MPC_4: 100,
      other_data: 120,
      created_at: 150,
      updated_at: 150,
      lead_history_count: 150,
    };
    return widthMap[key] || 150;
  }

  // Helper function to get minimum width
  function getMinWidth(key) {
    const minWidthMap = {
      id: 60,
      age: 60,
      marks: 80,
      MPC_4: 80,
      lead_history_count: 100,
    };
    return minWidthMap[key] || 100;
  }

  // Exclude system columns
  const sampleRow = dataToShow[0] ? {...dataToShow[0]} : {};
  const extendedSampleRow = sampleRow;

  const columns = dataToShow[0]
    ? Object.keys(extendedSampleRow)
        .filter((key) => 
          key !== 'Rowkey' && // Hide key
          key !== 'duplicate_count' && // Hide duplicate count
          key !== 'duplicate_ids' && // Hide duplicate IDs
          key !== 'is_duplicate_of_sent' && // Hide duplicate flag
          key !== 'current_data_id' && // Hide current data ID
          key !== 'original_data_id' // Hide original data ID
        )
        .sort((a, b) => {
          // Custom sort to match the report column order
          const order = [
            'id', // Move id to the first position
            'lead_history_count', // Move Lead History to second position
            'duplicate_indicator',
            'fullName',
            'phone',
            'age',
            'email',
            'country',
            'marks',
            'MPC_4',
            'data_source',
            'other_data',
            'integration_source',
            'api_success',
            'created_at',
            'updated_at',
            'error', // Move error to the very end
          ];
          const aIndex = order.indexOf(a);
          const bIndex = order.indexOf(b);
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return 0;
        })
        .map((key) => {
          const baseColumn = {
            field: key,
            headerName: key.replace(/_/g, ' ').toUpperCase(),
            editable:
              key !== 'id' && key !== 'created_at' && key !== 'updated_at',
            width: getColumnWidth(key),
            minWidth: getMinWidth(key),
            valueGetter: (params) => {
              if (['created_at', 'updated_at', 'sent_at'].includes(key)) {
                return externalFormatDate ? externalFormatDate(params.value) : formatDate(params.value);
              }
              return params.value;
            },
          };

          // Add special rendering for specific columns
          if (key === 'duplicate_indicator') {
            return {
              ...baseColumn,
              headerName: 'Duplicate',
              width: 120,
              minWidth: 100,
              editable: false,
              renderCell: (params) => (
                <DuplicateDataIndicator
                  isDuplicate={params.row.is_duplicate_of_sent === 1}
                  duplicateCount={params.row.duplicate_count || 0}
                  originalDataId={params.row.original_data_id}
                  currentDataId={params.row.current_data_id}
                />
              ),
            };
          }

          if (key === 'integration_source') {
            return {
              ...baseColumn,
              renderCell: (params) => (
                <IntegrationSourceChip integration_source={params.value} />
              ),
            };
          }

          if (key === 'api_success') {
            return {
              ...baseColumn,
              renderCell: (params) => (
                <ApiSuccessChip
                  integration_source={params.row.integration_source}
                  api_credential_id={params.row.api_credential_id}
                />
              ),
            };
          }

          // Remove the is_new column rendering logic since we're not showing it

          if (key === 'data_source') {
            return {
              ...baseColumn,
              renderCell: (params) => (
                <Chip
                  label={params.value || 'Unknown'}
                  size='small'
                  variant='outlined'
                  sx={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    border: '1px solid #1976d2',
                    fontSize: '0.75rem',
                    height: '24px',
                  }}
                />
              ),
            };
          }

          if (key === 'other_data') {
            return {
              ...baseColumn,
              width: 120,
              renderCell: (params) => (
                params.value ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleShowOtherData(params.value)}
                    sx={{
                      fontSize: '0.7rem',
                      height: '24px',
                      minWidth: '60px',
                    }}
                  >
                    Show
                  </Button>
                ) : (
                  <span style={{ color: '#ccc' }}>—</span>
                )
              ),
            };
          }

          if (key === 'lead_history_count') {
            return {
              ...baseColumn,
              renderCell: (params) => {
                const count = Number(params.value || 0);
                const title = count > 0 ? `${count} failure${count === 1 ? '' : 's'}` : 'No failures';
                return (
                  <Tooltip title={title}>
                    <span>
                      {count > 0 && (
                        <Chip
                          label={`×${count}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: '22px' }}
                        />
                      )}
                      <IconButton
                        size="small"
                        color={count > 0 ? 'error' : 'default'}
                        onClick={() => onOpenLeadHistory && onOpenLeadHistory(params.row)}
                        sx={{ ml: count > 0 ? 0.5 : 0 }}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              }
            };
          }

          return baseColumn;
        })
    : [];

  // Debug: Log the columns being generated
  console.log(
    '🔍 Generated columns:',
    columns.map((col) => col.field),
  );
  console.log(
    '🔍 Integration source column exists:',
    columns.some((col) => col.field === 'integration_source'),
  );
  console.log('🔍 Data sample:', dataToShow[0]);
  if (dataToShow[0] && dataToShow[0].integration_source) {
    console.log(
      '✅ Integration source value:',
      dataToShow[0].integration_source,
    );
  } else {
    console.log('❌ No integration_source in data');
  }

  // Function to get row class name for styling
  const getRowClassName = (params) => {
    if (params.row.is_duplicate_of_sent === 1) {
      return 'duplicate-row';
    }
    return '';
  };

  const handleEditRowsModelChange = React.useCallback(
    (model) => {
      console.log('model', model);

      setEditRowsModel(model);
      // Only update patchEdited if model is valid
      if (model && typeof model === 'object') {
        setPatchEdited(model);
      } else {
        setPatchEdited({});
      }
    },
    [setPatchEdited],
  );

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        {userRole.includes('admin') && (
          <GridToolbarExport csvOptions={{utf8WithBom: true}} />
        )}
      </GridToolbarContainer>
    );
  }

  // Show skeleton loading when getting data and no data to show
  if (gettingData && dataToShow.length === 0) {
    return <SkeletonDataGrid />;
  }

  return (
    <>
      <style>
        {`
          .duplicate-row {
            background-color: #fff3e0 !important;
          }
          .duplicate-row:hover {
            background-color: #ffe0b2 !important;
          }
        `}
      </style>
      <div style={{width: '100%'}}>
        <DataGrid
          autoHeight
          loading={false} // Disable default loading since we're using skeleton
          checkboxSelection
          rows={dataToShow.map((row) => ({
            ...row,
            api_success:
              row.integration_source && row.api_credential_id
                ? 'success'
                : null,
          }))}
          getRowId={(row) => row.id}
          selectionModel={selected || []}
          onSelectionModelChange={(newSelection) => {
            console.log('🔴 DataGrid onSelectionModelChange called with:', newSelection);
            console.log('🔴 Current selected prop:', selected);
            console.log('🔴 Available row IDs:', dataToShow.map((row) => row.id));
            handleOnSelect(newSelection);
          }}
          onRowDoubleClick={handleRowDoubleClick}
          rowCount={rowCount}
          columns={columns} // Apply filtered and formatted columns
          editRowsModel={editRowsModel}
          editMode='row'
          disableSelectionOnClick
          components={{
            Toolbar: CustomToolbar,
          }}
          onEditRowsModelChange={handleEditRowsModelChange}
          page={pageNumber}
          pageSize={pageSize}
          onPageChange={setPageNumber}
          onPageSizeChange={setPageSize}
          paginationMode='server'
          rowsPerPageOptions={[10, 20, 50, 100]}
          onRowEditCommit={(params) => {
            // Don't call onEditCommit immediately, just store the changes
            console.log('Row edit committed:', params);
          }}
          getRowClassName={getRowClassName}
        />
      </div>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCancelEdit}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <EditIcon />
            Edit Data Entry
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              pt: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
            }}
          >
            {editingRow &&
              Object.keys(editFormData).map((key) => {
                // Exclude system fields, customer fields, domain fields, and integration source
                const excludedFields = [
                  'id',
                  'created_at',
                  'updated_at',
                  'Rowkey',
                  'sent_at',
                  'customer_name',
                  'customer_id',
                  'customerId',
                  'user_name',
                  'userName',
                  'custmoer_name',
                  'custmoerName',
                  'custmoer_id',
                  'custmoerId',
                  'Custmoer_Name',
                  'CUSTMOER_NAME',
                  'CustmoerName',
                  'domain_name',
                  'domain_id',
                  'domin_id',
                  'domainId',
                  'dominId',
                  'integration_source',
                  'formId',
                  'api_success',
                  'duplicate_count',
                  'duplicate_ids',
                  'is_duplicate_of_sent',
                  'current_data_id',
                  'original_data_id',
                  'lead_history_count',
                ];

                // Check if key contains 'customer' or 'user' and exclude it
                if (
                  excludedFields.includes(key) ||
                  key.toLowerCase().includes('customer') ||
                  key.toLowerCase().includes('user') ||
                  key.toLowerCase().includes('custmoer') ||
                  key.toLowerCase().includes('custmer') ||
                  key.toLowerCase().includes('custmor')
                ) {
                  return null;
                }

                // Special handling for other_data - show as textarea
                if (key === 'other_data') {
                  const displayValue = editFormData[key]
                    ? typeof editFormData[key] === 'object'
                      ? JSON.stringify(editFormData[key], null, 2)
                      : editFormData[key]
                    : '';

                  return (
                    <TextField
                      key={key}
                      label={key.replace(/_/g, ' ').toUpperCase()}
                      value={displayValue}
                      onChange={(e) =>
                        handleEditFormChange(key, e.target.value)
                      }
                      fullWidth
                      size='small'
                      multiline
                      rows={4}
                      helperText='JSON format: {"key": "value"} or plain text'
                      sx={{gridColumn: 'span 2'}}
                    />
                  );
                }

                return (
                  <TextField
                    key={key}
                    label={key.replace(/_/g, ' ').toUpperCase()}
                    value={editFormData[key] || ''}
                    onChange={(e) => handleEditFormChange(key, e.target.value)}
                    fullWidth
                    size='small'
                    disabled={key === 'integration_source'}
                    helperText={
                      key === 'integration_source'
                        ? 'This field cannot be edited'
                        : ''
                    }
                  />
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant='contained'
            startIcon={<SaveIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Other Data Modal */}
      <Dialog
        open={otherDataModalOpen}
        onClose={handleCloseOtherDataModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Other Data</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              maxHeight: '400px',
              overflow: 'auto',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
          >
            {otherDataContent ? (
              typeof otherDataContent === 'object'
                ? JSON.stringify(otherDataContent, null, 2)
                : otherDataContent
            ) : 'No data available'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOtherDataModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

RowEditControlGrid.propTypes = {
  dataToShow: PropTypes.array.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPageSize: PropTypes.func.isRequired,
  pageNumber: PropTypes.number.isRequired,
  setPageNumber: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onEditCommit: PropTypes.func.isRequired,
  setPatchEdited: PropTypes.func.isRequired,
  gettingData: PropTypes.bool.isRequired,
  handleOnSelect: PropTypes.func.isRequired,
  userRole: PropTypes.array.isRequired,
  customerMapping: PropTypes.object,
  formatDate: PropTypes.func, // Optional external formatter
  selected: PropTypes.array, // Selected row IDs
  onOpenLeadHistory: PropTypes.func, // Handler for opening lead history
};
